import Component from '@glimmer/component'
import { service } from '@ember/service'
import { isPresent } from '@ember/utils'
import { trackedFunction } from 'reactiveweb/function'
import {
  tableColumnsToComparerKeyMapping,
  injectComparerKeys,
  type SortingConfig,
  type SortComparerKey,
} from '@blakeelearning/data-tables/utils/sorting'
import { Interval } from 'teacher-ui-client/components/stable-table/basic-cells/duration-unit'
import { type IntlService, t } from 'ember-intl'
import type DataTransformerService from '@blakeelearning/data-tables/services/data-transformer'
import type { DurationConfig } from 'teacher-ui-client/components/stable-table/basic-table'
import type { SafeString } from '@ember/template'
import type { PipelineDescriptor } from '@blakeelearning/data-tables/utils/data-transformer'
import type { RowObject } from 'teacher-ui-client/components/stable-table/basic-table/cell'
import BasicTable from 'teacher-ui-client/components/stable-table/basic-table'
import SortableCell from 'teacher-ui-client/components/stable-table/basic-cells/sortable'
import DurationUnitCell from 'teacher-ui-client/components/stable-table/basic-cells/duration-unit'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import { FidgetSpinnerWaveComponent } from '@blakeelearning/fidget/components/fidget/spinner/wave'
import { and, not } from 'ember-truth-helpers'
import { hash } from '@ember/helper'
import type { FormatValueOptions } from 'teacher-ui-client/utils/format-value'

interface Args {
  items?: FluencySprintsTeacherItem[]
  isLoadingItems?: boolean
  onSortClick?: (sortObject: SortObject) => void
  sortingConfig?: SortingConfig
  exportable?: boolean
  tableTooltipText?: string
}

interface Signature {
  Args: Args
}

interface Column {
  value?: string | SafeString
  cellClassNames?: string
  valuePath?: string
  sortKey?: string
  comparerKey?: SortComparerKey
  classNames?: string
  component?: typeof SortableCell
  cellComponent?: typeof DurationUnitCell
  formatEmptyConfig?: FormatValueOptions
  lessonId?: string
  durationConfig?: DurationConfig
}

export interface FluencySprintsTeachersAggregate {
  time_on_task: {
    average: number
    total: number
  }
  sprints_mastered: {
    average: number
    total: number
  }
  sprints_attempted: {
    average: number
    total: number
  }
  correct_answers: {
    average: number
    total: number
  }
  scores: {
    average_first_score: number
    average_best_score: number
    average_improvement: number
  }
}

export interface FluencySprintsTeacherItem {
  id: string
  firstName: string
  lastName: string
  grades: string
  aggregate: FluencySprintsTeachersAggregate
}

export class FluencySprintsTeachers extends Component<Signature> {
  @service intl!: IntlService
  @service dataTransformer!: DataTransformerService

  get isUpdating(): boolean {
    return this.args.isLoadingItems || this.transformedItems.isPending
  }

  get pipelineDescriptors(): Nullable<PipelineDescriptor>[] {
    return [this.sortingDescriptor]
  }

  get sortingDescriptor() {
    if (!this.args.sortingConfig) return null

    const comparerKeyMapping = tableColumnsToComparerKeyMapping(this.columns)
    const sortingConfigWithComparerKeys = injectComparerKeys(this.args.sortingConfig, comparerKeyMapping)
    const sortDescriptor = { type: 'sort' as const, config: sortingConfigWithComparerKeys }

    return sortDescriptor
  }

  transformedItems = trackedFunction(this, async (): Promise<FluencySprintsTeacherItem[]> => {
    const presentDescriptors = this.pipelineDescriptors.filter(isPresent)
    const transformedData = await this.dataTransformer.buildAndTransform<
      FluencySprintsTeacherItem,
      FluencySprintsTeacherItem
    >(
      [...(this.args.items ?? [])], // spread to entangle with each item in the tracked array as they are added
      presentDescriptors,
    )
    return transformedData.items
  })

  /**
   * Defines how the durations should be formatted
   */
  get durationConfig() {
    const splitDurationOptions = {
      max: Interval.Days,
      min: Interval.Minutes,
      maxIntervals: 2,
    }
    const dataInterval = 'seconds'
    return { dataInterval, splitDurationOptions }
  }

  get columns(): Column[] {
    const { durationConfig, intl } = this
    const translationPrefix = 'tables.fluencySprints.teachers.headers'

    const defaultColumn: Column = {
      classNames: 'table-box table-box-header bg-ms-green-100',
      cellClassNames: 'table-box table-box-cell bg-ms-green-50 group-hover:bg-ms-green-100',
      component: SortableCell,
      comparerKey: 'sortByNumber',
    }

    const columns: Column[] = [
      {
        ...defaultColumn,
        cellClassNames: `${defaultColumn.cellClassNames} print:wrap-break-word`,
        value: intl.t(`${translationPrefix}.firstName`),
        valuePath: 'firstName',
        comparerKey: 'sortByValue',
      },
      {
        ...defaultColumn,
        cellClassNames: `${defaultColumn.cellClassNames} print:wrap-break-word`,
        value: intl.t(`${translationPrefix}.lastName`),
        valuePath: 'lastName',
        comparerKey: 'sortByValue',
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.grades`),
        valuePath: 'grades',
        comparerKey: 'sortByValue', // would normally be sortByGrade, but this will be multiple grades.
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.timeOnTask`),
        valuePath: 'aggregate.time_on_task.total',
        cellComponent: DurationUnitCell,
        durationConfig,
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.totalSprintsMastered`),
        valuePath: 'aggregate.sprints_mastered.total',
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.totalQuestionsAnswered`),
        valuePath: 'aggregate.correct_answers.total',
      },
      {
        ...defaultColumn,
        value: intl.t(`${translationPrefix}.averageSprintsAttempted`),
        valuePath: 'aggregate.sprints_attempted.average',
      },
      {
        ...defaultColumn,
        classNames: `${defaultColumn.classNames} whitespace-nowrap print:wrap-break-word`,
        value: intl.t(`${translationPrefix}.averageImprovement`),
        valuePath: 'aggregate.scores.average_improvement',
        formatEmptyConfig: { append: '%' },
      },
    ]

    return columns
  }

  get teacherRows() {
    return (
      this.transformedItems.value?.map((item) => ({
        ...item,
        classNames: 'table-row-striped group',
        cells: [{}, {}, {}, {}, {}, {}, {}, {}],
      })) ?? []
    )
  }

  /**
   * This function will round a number to 1 decimal place, unless that number equals a whole number, then it will
   * use the whole number value
   * 15.123 -> 15.1
   * 15.0123 -> 15 (not 15.0)
   */
  roundTo1Dp(number: number) {
    return Math.round(number * 10) / 10
  }

  get footerRows() {
    const { teacherRows, durationConfig, intl } = this

    // Footer aggregates need to be calculated here, since we can't get the values from the backend currently.
    const totalTimeOnTask = { average: 0, total: 0 }
    const totalSprintsMastered = { average: 0, total: 0 }
    const totalQuestionsAnswered = { average: 0, total: 0 }
    const averageSprintsAttempted = { average: 0, total: 0 }
    const averageImprovement = { average: 0, total: 0 }
    if (teacherRows.length > 0) {
      teacherRows.forEach((item) => {
        totalTimeOnTask.total += item.aggregate.time_on_task.total
        totalSprintsMastered.total += item.aggregate.sprints_mastered.total
        totalQuestionsAnswered.total += item.aggregate.correct_answers.total
        averageSprintsAttempted.total += item.aggregate.sprints_attempted.average
        averageImprovement.total += item.aggregate.scores.average_improvement
      })
      totalTimeOnTask.average = Math.round(totalTimeOnTask.total / teacherRows.length)
      totalSprintsMastered.average = this.roundTo1Dp(totalSprintsMastered.total / teacherRows.length)
      totalQuestionsAnswered.average = this.roundTo1Dp(totalQuestionsAnswered.total / teacherRows.length)
      averageSprintsAttempted.average = this.roundTo1Dp(averageSprintsAttempted.total / teacherRows.length)
      averageImprovement.average = this.roundTo1Dp(averageImprovement.total / teacherRows.length)
    }

    // For the known empty cells in the footer, we want no text displayed
    const emptyCell = { formatEmptyConfig: { replacement: '' } }

    const averageRow: RowObject = {
      cells: [
        { value: intl.t('tables.fluencySprints.teachers.footers.average') },
        emptyCell,
        emptyCell,
        { component: DurationUnitCell, durationConfig, value: totalTimeOnTask.average },
        { value: totalSprintsMastered.average },
        { value: totalQuestionsAnswered.average },
        { value: averageSprintsAttempted.average },
        { value: averageImprovement.average, formatEmptyConfig: { append: '%' } },
      ],
    }

    const totalsRow: RowObject = {
      cells: [
        { value: intl.t('tables.fluencySprints.teachers.footers.totals') },
        emptyCell,
        emptyCell,
        { component: DurationUnitCell, durationConfig, value: totalTimeOnTask.total },
        { value: totalSprintsMastered.total },
        { value: totalQuestionsAnswered.total },
        emptyCell,
        emptyCell,
      ],
    }

    const classNames = 'table-footer-cell border-t border-ms-green-100 print:border-black'
    averageRow.cells?.forEach((cell) => {
      cell.classNames = classNames
    })
    totalsRow.cells?.forEach((cell) => {
      cell.classNames = classNames
    })

    return [averageRow, totalsRow]
  }

  <template>
    <BasicTable
      class="relative w-full"
      exportable={{@exportable}}
      @columns={{this.columns}}
      @rows={{this.teacherRows}}
      @footerRows={{this.footerRows}}
      @config={{hash sortingConfig=@sortingConfig onSortClick=@onSortClick}}
      as |bt|
    >
      <bt.head as |h|>
        {{! Custom grouped header }}
        <tr data-test-grouped-header-row>
          <th class="p-3 print:pl-0" colspan="6">
            {{#if @tableTooltipText}}
              <span class="float-right print:hidden">
                <PhosphorIcon @icon="phosphor-info-fill" class="text-dusty-black-500 cursor-pointer" />
                <Tooltip @text={{@tableTooltipText}} />
              </span>
            {{/if}}
            <div class="font-bold">{{t "tables.fluencySprints.teachers.headers.mentalMinute"}}</div>
            <div class="font-normal">{{t "tables.fluencySprints.teachers.headers.numberFactFluency"}}</div>
          </th>
          <th class="table-box table-box-header bg-ms-green-150 text-center" colspan="2">
            {{t "tables.fluencySprints.teachers.headers.numberFactsPerMinute"}}
          </th>
        </tr>
        {{! Standard header }}
        <h.row />
      </bt.head>

      {{! If not updating and no students to show, always only display the empty message }}
      {{#if (and (not this.isUpdating) (not this.teacherRows))}}
        <bt.spanner class="p-3 text-center italic">
          {{t "tables.fluencySprints.emptyTableMessage"}}
        </bt.spanner>
      {{else}}
        {{! If updating, always show a loading spinner}}
        {{! When no items, show it normally. }}
        {{! If there are items, place it on top of the existing table body (absolute) }}
        {{#if this.isUpdating}}
          <bt.spanner
            class="table-box table-box-cell border-transparent
              {{if this.teacherRows 'absolute left-1/2 -translate-x-1/2 transform'}}"
          >
            <FidgetSpinnerWaveComponent @small={{true}} @centered={{true}} />
          </bt.spanner>
        {{/if}}
        <bt.body class={{if this.isUpdating "opacity-50"}} />
        <bt.foot />
      {{/if}}

    </BasicTable>
  </template>
}

export default FluencySprintsTeachers
